import React, { useState, useEffect } from "react";
import {
  sendMagicLink,
  logUserConsent,
  verifyOtpCode,
} from "../../services/auth/authService";
import { useJWTAuth } from "../../context/JWTAuthContext";
import { useTheme } from "../../hooks/useTheme";
import { Link, useNavigate } from "react-router-dom";
import packageJson from "../../../package.json";
import logger from "../../utils/logger";
import MaintenanceBanner from "../../components/MaintenanceBanner";
import BrowserAdvice from "../../components/BrowserAdvice";
import { logAuthConsents } from "./consentLogging";

const SUPPORT_CONTACTS = [
  {
    href: "https://t.me/jywru",
    label: "Telegram",
    helper: "Написать в Telegram",
  },
  {
    href: "https://vk.com/luvjoy",
    label: "VK",
    helper: "Написать во ВКонтакте",
  },
  {
    href:
      "mailto:i@5553398.ru?subject=" +
      encodeURIComponent('Нужна помощь в приложении "Мои горизонты"'),
    label: "E-mail",
    helper: "Написать на почту",
  },
];

const SupportContactButtons = ({ isMailRu }) => (
  <div className="mt-4">
    <p className="mb-2 text-center text-[11px] font-bold uppercase tracking-wider text-[#4F46E5] dark:text-[#818CF8]">
      {isMailRu
        ? "Если письмо не приходит, выберите другой способ связи"
        : "Нужна помощь? Выберите удобный способ связи"}
    </p>
    <div className="flex flex-col gap-2">
      {SUPPORT_CONTACTS.map((contact) => (
        <a
          key={contact.label}
          href={contact.href}
          target={contact.href.startsWith("mailto:") ? undefined : "_blank"}
          rel={contact.href.startsWith("mailto:") ? undefined : "noopener noreferrer"}
          className="flex items-center justify-center gap-2 rounded-xl bg-indigo-50 dark:bg-indigo-900/20 p-3 text-center text-[#4F46E5] dark:text-[#818CF8] border border-indigo-100 dark:border-indigo-800/30 hover:bg-indigo-100 dark:hover:bg-indigo-900/40 transition-all duration-300"
        >
          <span className="text-xs font-bold uppercase tracking-wider">
            {contact.label}
          </span>
          <span className="text-[11px] font-medium">
            {contact.helper}
          </span>
        </a>
      ))}
    </div>
  </div>
);

const ThemeToggle = () => {
  const { theme, toggleTheme } = useTheme();

  return (
    <button
      onClick={toggleTheme}
      className="p-2 rounded-full bg-slate-100 dark:bg-slate-800 text-slate-500 dark:text-slate-400 hover:bg-slate-200 dark:hover:bg-slate-700 transition-colors"
      aria-label="Переключить тему"
    >
      {theme === "dark" ? (
        <svg
          xmlns="http://www.w3.org/2000/svg"
          className="h-5 w-5 text-amber-400"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <circle cx="12" cy="12" r="4" />
          <path d="M12 2v2" />
          <path d="M12 20v2" />
          <path d="m4.93 4.93 1.41 1.41" />
          <path d="m17.66 17.66 1.41 1.41" />
          <path d="M2 12h2" />
          <path d="M20 12h2" />
          <path d="m6.34 17.66-1.41 1.41" />
          <path d="m19.07 4.93-1.41 1.41" />
        </svg>
      ) : (
        <svg
          xmlns="http://www.w3.org/2000/svg"
          className="h-5 w-5 text-indigo-500"
          viewBox="0 0 20 20"
          fill="currentColor"
        >
          <path d="M17.293 13.293A8 8 0 016.707 2.707a8.001 8.001 0 1010.586 10.586z" />
        </svg>
      )}
    </button>
  );
};

/**
 * Страница входа через пинкод
 *
 * Процесс авторизации:
 * 1. Пользователь вводит email
 * 2. Приложение отправляет OTP код на email
 * 3. Пользователь вводит 6-значный пинкод
 * 4. При успешной верификации происходит авторизация
 */
const SimpleLoginPage = () => {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState("");
  const [magicLinkSent, setMagicLinkSent] = useState(false);
  const [countdown, setCountdown] = useState(0);
  const [rateLimitSecs, setRateLimitSecs] = useState(0);
  const [agreedTerms, setAgreedTerms] = useState(false);
  const [agreedConsent, setAgreedConsent] = useState(false);
  const [agreedPrivacy, setAgreedPrivacy] = useState(false);
  const [otpCode, setOtpCode] = useState("");
  const [rememberMe, setRememberMe] = useState(true);

  const navigate = useNavigate();
  const { isAuthenticated } = useJWTAuth();

  const isMailRu = (email) => {
    const mailRuDomains = [
      "mail.ru",
      "bk.ru",
      "inbox.ru",
      "list.ru",
      "internet.ru",
    ];
    const domain = email?.split("@")[1]?.toLowerCase();
    return mailRuDomains.includes(domain);
  };

  // Redirect if already authenticated

  useEffect(() => {
    if (isAuthenticated) {
      navigate("/dashboard");
    }
  }, [isAuthenticated, navigate]);

  // Timer effect
  useEffect(() => {
    let timer;
    if (countdown > 0) {
      timer = setInterval(() => {
        setCountdown((prev) => prev - 1);
      }, 1000);
    }
    return () => clearInterval(timer);
  }, [countdown]);

  // Helper to translate technical errors to user-friendly Russian messages
  const translateErrorMessage = (errorMessage) => {
    if (!errorMessage) return "Произошла неизвестная ошибка";

    const lowerMsg = errorMessage.toLowerCase();

    if (
      lowerMsg.includes("rate limit") ||
      lowerMsg.includes("many requests") ||
      lowerMsg.includes("for security purposes") ||
      lowerMsg.includes("only request this after")
    ) {
      const m = lowerMsg.match(/after (\d+) seconds/);
      const secs = m ? parseInt(m[1], 10) : 15;
      return "Слишком много попыток. Пожалуйста, подождите " + secs + " секунд и повторите.";
    }
    if (lowerMsg.includes("user not found")) {
      return "Пользователь с таким email не найден.";
    }
    if (
      lowerMsg.includes("invalid login credentials") ||
      lowerMsg.includes("invalid authentication credentials")
    ) {
      return "Неверные данные для входа.";
    }
    if (
      lowerMsg.includes("error unmarshaling") ||
      lowerMsg.includes("send email output")
    ) {
      return "Ошибка отправки письма. Попробуйте еще раз через минуту.";
    }
    if (
      lowerMsg.includes("error running hook uri") ||
      lowerMsg.includes("unexpected_failure") ||
      lowerMsg.includes("hook uri")
    ) {
      return "Письмо, возможно, уже отправлено, но подтверждение от сервера не пришло. Если код придет, введите его ниже.";
    }
    if (
      lowerMsg.includes("network error") ||
      lowerMsg.includes("failed to fetch")
    ) {
      return "Ошибка сети. Если отправка пин-кода долго не производится, отключите VPN, повторите попытку или перезагрузите приложение.";
    }
    if (
      lowerMsg.includes("token has expired") ||
      lowerMsg.includes("invalid token")
    ) {
      return "Код истек или неверен. Запросите новый код.";
    }

    // Return original message if it seems to be already localized (contains Cyrillic)
    if (/[а-яА-ЯёЁ]/.test(errorMessage)) {
      return errorMessage;
    }

    return "Произошла ошибка. Попробуйте позже.";
  };

  const sendCode = async (isResend = false) => {
    if (!email.trim()) {
      setMessage("Пожалуйста, введите email");
      return;
    }

    // Email валидация
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      setMessage("Неверный формат email");
      return;
    }

    setLoading(true);
    setMessage("");

    try {
      // 2. Логируем согласия - ОТЛОЖЕНО до момента авторизации (AuthCallback или handleOtpVerify)
      // Это необходимо, так как RLS требует аутентификации для записи в user_consents.

      // Передаем только отправку Magic Link
      const result = await sendMagicLink(email);

      if (!result.success) {
        throw new Error(result.error || "Не удалось отправить письмо");
      }

      setMagicLinkSent(true);

      setMessage(
        isResend
          ? `Новый код отправлен на ${email}. Вводите код из ПОСЛЕДНЕГО письма — предыдущие коды уже не действуют.`
          : result.deliveryAmbiguous
            ? result.warning ||
              `Письмо, возможно, отправлено на ${email}. Если код придет, введите его ниже. Проверьте папку «Спам» — почтовые сервисы иногда задерживают письма с кодами.`
            : `Письмо отправлено на ${email}. Введите код из письма. Если письма нет в течение минуты — проверьте папку «Спам» (в Gmail — также «Промоакции»). Не запрашивайте код повторно: каждый новый запрос отменяет предыдущий.`,
      );
      setCountdown(60);
    } catch (err) {
      // FIX: Handle 401 as success due to backend quirk
      if (
        err.message &&
        (err.message.includes("Invalid authentication credentials") ||
          err.message.includes("401"))
      ) {
        setMagicLinkSent(true);
        setMessage(`Письмо отправлено на ${email}. Введите код из письма.`);
        setCountdown(60);
        return;
      }

      const translated = translateErrorMessage(err.message);
      setMessage(translated);
      // Если это rate-limit (429) — блокируем кнопку на нужное время
      const m = (err.message || "").toLowerCase().match(/after (\d+) seconds/);
      if (m) {
        const secs = parseInt(m[1], 10);
        setRateLimitSecs(secs);
        setCountdown(secs);
        // Заменяем сообщение на более понятное — с явным указанием, что письмо скорее всего уже ушло
        setMessage(
          `Слишком частые запросы. Предыдущее письмо с кодом уже отправлено — подождите ${secs} сек, проверьте папку «Спам» и введите код из ПОСЛЕДНЕГО письма.`,
        );
      }
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    sendCode(false);
  };

  const handleResend = () => {
    // Повторная отправка: предыдущий код аннулируется, поле очищаем
    setOtpCode("");
    sendCode(true);
  };

  const handleOtpVerify = async (e) => {
    e.preventDefault();
    logger.info("handleOtpVerify started");

    if (!otpCode || otpCode.length < 6) {
      setMessage("Введите 6-значный код");
      return;
    }

    setLoading(true); // Reuse loading or add otpLoading

    try {
      logger.debug("handleOtpVerify values", {
        email,
        otpCode,
        rememberMe,
      });
      const result = await verifyOtpCode(email, otpCode, rememberMe);
      logger.debug("handleOtpVerify result", result);

      if (result.success) {
        try {
          const userEmail = result.user.email;

          // Логируем согласия (поскольку пользователь прошел экран входа со всеми галочками)
          await logAuthConsents({ email: userEmail, logUserConsent });
          logger.info("Consents logged after login");
        } catch (consentErr) {
          logger.error("Consent logging error", consentErr);
        }

        // Прямой вызов login() удален, так как onAuthStateChange в JWTAuthProvider
        // автоматически обновит состояние при успешном verifyOtpCode.
        // Мы просто ждем isAuthenticated через useEffect для редиректа.
        logger.info("Login completed, waiting for auth state update");
      } else {
        setMessage(translateErrorMessage(result.error));
      }
    } catch (err) {
      logger.error("handleOtpVerify error", err);
      setMessage(translateErrorMessage(err.message));
    } finally {
      setLoading(false);
    }
  };

  // Подсказка при долгой проверке — сеть медленная, но мы ещё ждём ответ
  const [slowVerifyHint, setSlowVerifyHint] = useState(false);
  useEffect(() => {
    if (!loading) {
      setSlowVerifyHint(false);
      return;
    }
    const t = setTimeout(() => setSlowVerifyHint(true), 8000);
    return () => clearTimeout(t);
  }, [loading]);

  const handleEmailChange = (e) => {
    setEmail(e.target.value);
    setMessage("");
    setMagicLinkSent(false);
  };

  return (
    <div className="relative min-h-screen flex items-center justify-center bg-[#EEF2FF] dark:bg-[#0A0E27] overflow-hidden font-sans pt-12 pb-12 transition-colors duration-500">
      {/* Animated Background Blobs */}
      <div className="absolute top-0 left-0 w-full h-full overflow-hidden z-0">
        <div className="absolute top-[-10%] left-[-10%] w-[500px] h-[500px] bg-blue-400 dark:bg-[#4F46E5] rounded-full mix-blend-multiply filter blur-[100px] opacity-20 dark:opacity-20 animate-blob"></div>
        <div className="absolute top-[-10%] right-[-10%] w-[500px] h-[500px] bg-purple-400 dark:bg-[#818CF8] rounded-full mix-blend-multiply filter blur-[100px] opacity-20 dark:opacity-20 animate-blob animation-delay-2000"></div>
        <div className="absolute bottom-[-20%] left-[20%] w-[500px] h-[500px] bg-pink-400 dark:bg-[#F97316] rounded-full mix-blend-multiply filter blur-[100px] opacity-20 dark:opacity-10 animate-blob animation-delay-4000"></div>
      </div>

      <div className="relative z-10 w-full max-w-md px-[var(--mobile-content-padding)] sm:px-4">
        <MaintenanceBanner className="mb-4" />

        {/* Main Card */}
        <div className="bg-white/80 dark:bg-[#121212]/90 backdrop-blur-2xl border border-white/20 dark:border-white/10 rounded-[2.5rem] shadow-[0_20px_50px_rgba(0,0,0,0.1)] dark:shadow-[0_20px_50px_rgba(0,0,0,0.3)] overflow-hidden p-10 animate-fade-in-up">
          <div className="text-center mb-10 relative">
            {/* Theme Toggle Absolute Position */}
            <div className="absolute top-0 right-0">
              <ThemeToggle />
            </div>

            <div className="inline-flex items-center justify-center w-20 h-20 bg-gradient-to-br from-[#4F46E5] to-[#818CF8] rounded-3xl shadow-lg shadow-indigo-500/30 mb-6 transform hover:scale-105 transition-transform duration-300">
              <span className="text-4xl">🍽️</span>
            </div>
            <h1 className="text-3xl font-black tracking-tight text-[#1E1B4B] dark:text-white mb-2">
              Мои Горизонты
            </h1>
            <p className="text-[#4F46E5] dark:text-[#818CF8] font-bold tracking-wide uppercase text-xs">
              Мои Горизонты
            </p>
          </div>

          <BrowserAdvice />

          {!magicLinkSent ? (
            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="space-y-2 text-center">
                <h2 className="text-xl font-bold text-[#1E1B4B] dark:text-white">
                  Добро пожаловать
                </h2>
                <p className="text-sm text-slate-500 dark:text-slate-400">
                  Введите email для получения кода доступа
                </p>
              </div>

              <div className="space-y-2">
                <label className="block text-xs font-bold uppercase tracking-wider text-slate-400 dark:text-slate-500 ml-1">
                  Электронная почта
                </label>
                <div className="relative group">
                  <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none text-slate-400 group-focus-within:text-[#4F46E5] transition-colors">
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      viewBox="0 0 20 20"
                      fill="currentColor"
                      className="w-5 h-5"
                    >
                      <path d="M3 4a2 2 0 00-2 2v1.161l8.441 4.221a1.25 1.25 0 001.118 0L19 7.162V6a2 2 0 00-2-2H3z" />
                      <path d="M19 8.839l-7.77 3.885a2.75 2.75 0 01-2.46 0L1 8.839V14a2 2 0 002 2h14a2 2 0 002-2V8.839z" />
                    </svg>
                  </div>
                  <input
                    type="email"
                    value={email}
                    onChange={handleEmailChange}
                    placeholder="example@mail.ru"
                    disabled={loading || countdown > 0}
                    required
                    className="w-full pl-12 pr-4 py-4 bg-slate-50 dark:bg-[#1A1A1A] border border-slate-200 dark:border-white/5 rounded-2xl focus:ring-4 focus:ring-[#4F46E5]/20 focus:border-[#4F46E5] transition-all outline-none text-[#1E1B4B] dark:text-white placeholder:text-slate-400 font-medium"
                  />
                </div>
              </div>

              {message && (
                <div
                  className={`p-4 rounded-2xl text-sm text-center font-bold flex flex-col items-center justify-center gap-2 ${
                    message.includes("отправлено") || message.includes("✅")
                      ? "bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400 border border-green-200 dark:border-green-800"
                      : "bg-red-50 dark:bg-red-900/20 text-red-600 dark:text-red-400 border border-red-200 dark:border-red-800"
                  }`}
                >
                  <div className="flex items-center gap-2">
                    {message.includes("отправлено") ||
                    message.includes("✅") ? (
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        className="h-5 w-5 shrink-0"
                        viewBox="0 0 20 20"
                        fill="currentColor"
                      >
                        <path
                          fillRule="evenodd"
                          d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                          clipRule="evenodd"
                        />
                      </svg>
                    ) : (
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        className="h-5 w-5 shrink-0"
                        viewBox="0 0 20 20"
                        fill="currentColor"
                      >
                        <path
                          fillRule="evenodd"
                          d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z"
                          clipRule="evenodd"
                        />
                      </svg>
                    )}
                    <span>{message}</span>
                  </div>

                  {!(
                    message.includes("отправлено") || message.includes("✅")
                  ) &&
                    isMailRu(email) && (
                      <div className="mt-2 text-xs font-medium text-red-500 dark:text-red-300 leading-relaxed border-t border-red-200/50 dark:border-red-800/50 pt-2">
                        ⚠️ Замечена проблема с доставкой на Mail.ru. Если письмо
                        не приходит, попросите разработчика заменить почту на
                        другую.
                      </div>
                    )}
                </div>
              )}

              {message &&
                !message.includes("отправлено") &&
                !message.includes("✅") && (
                  <SupportContactButtons isMailRu={isMailRu(email)} />
                )}

              <div className="space-y-3 bg-slate-50/50 dark:bg-white/5 p-4 rounded-3xl border border-slate-100 dark:border-white/5">
                <div
                  className="flex items-center gap-3 p-1 cursor-pointer group"
                  onClick={() => setAgreedTerms(!agreedTerms)}
                >
                  <div
                    className={`w-6 h-6 shrink-0 rounded-lg border-2 flex items-center justify-center transition-all ${agreedTerms ? "bg-[#4F46E5] border-[#4F46E5]" : "border-slate-300 dark:border-slate-600 group-hover:border-[#4F46E5]"}`}
                  >
                    {agreedTerms && (
                      <svg
                        className="w-4 h-4 text-white"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                        strokeWidth="3"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          d="M5 13l4 4L19 7"
                        />
                      </svg>
                    )}
                  </div>
                  <div className="text-[11px] font-bold text-slate-600 dark:text-slate-400 leading-tight">
                    Я принимаю{" "}
                    <Link
                      to="/terms"
                      className="text-[#4F46E5] hover:underline"
                      onClick={(e) => e.stopPropagation()}
                    >
                      Пользовательское соглашение
                    </Link>
                  </div>
                </div>

                <div
                  className="flex items-center gap-3 p-1 cursor-pointer group"
                  onClick={() => setAgreedConsent(!agreedConsent)}
                >
                  <div
                    className={`w-6 h-6 shrink-0 rounded-lg border-2 flex items-center justify-center transition-all ${agreedConsent ? "bg-[#4F46E5] border-[#4F46E5]" : "border-slate-300 dark:border-slate-600 group-hover:border-[#4F46E5]"}`}
                  >
                    {agreedConsent && (
                      <svg
                        className="w-4 h-4 text-white"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                        strokeWidth="3"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          d="M5 13l4 4L19 7"
                        />
                      </svg>
                    )}
                  </div>
                  <div className="text-[11px] font-bold text-slate-600 dark:text-slate-400 leading-tight">
                    Я даю согласие на{" "}
                    <Link
                      to="/consent"
                      className="text-[#4F46E5] hover:underline"
                      onClick={(e) => e.stopPropagation()}
                    >
                      обработку персональных данных
                    </Link>
                  </div>
                </div>

                <div
                  className="flex items-center gap-3 p-1 cursor-pointer group"
                  onClick={() => setAgreedPrivacy(!agreedPrivacy)}
                >
                  <div
                    className={`w-6 h-6 shrink-0 rounded-lg border-2 flex items-center justify-center transition-all ${agreedPrivacy ? "bg-[#4F46E5] border-[#4F46E5]" : "border-slate-300 dark:border-slate-600 group-hover:border-[#4F46E5]"}`}
                  >
                    {agreedPrivacy && (
                      <svg
                        className="w-4 h-4 text-white"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                        strokeWidth="3"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          d="M5 13l4 4L19 7"
                        />
                      </svg>
                    )}
                  </div>
                  <div className="text-[11px] font-bold text-slate-600 dark:text-slate-400 leading-tight">
                    Я ознакомлен с{" "}
                    <Link
                      to="/privacy"
                      className="text-[#4F46E5] hover:underline"
                      onClick={(e) => e.stopPropagation()}
                    >
                      Политикой конфиденциальности
                    </Link>
                  </div>
                </div>
              </div>

              <div className="pt-2">
                <button
                  type="submit"
                  disabled={
                    loading ||
                    !email.trim() ||
                    countdown > 0 ||
                    !agreedTerms ||
                    !agreedConsent ||
                    !agreedPrivacy
                  }
                  className={`
                    w-full py-4 px-6 rounded-2xl font-black text-white shadow-xl
                    transition-all duration-300 transform active:scale-95
                    ${
                      loading ||
                      !email.trim() ||
                      countdown > 0 ||
                      !agreedTerms ||
                      !agreedConsent ||
                      !agreedPrivacy
                        ? "bg-slate-300 dark:bg-slate-800 text-slate-500 cursor-not-allowed grayscale"
                        : "bg-gradient-to-r from-[#4F46E5] to-[#818CF8] hover:shadow-indigo-500/40 hover:-translate-y-1"
                    }
                  `}
                >
                  {loading ? (
                    <div className="flex items-center justify-center gap-2">
                      <svg
                        className="animate-spin h-5 w-5 text-white"
                        xmlns="http://www.w3.org/2000/svg"
                        fill="none"
                        viewBox="0 0 24 24"
                      >
                        <circle
                          className="opacity-25"
                          cx="12"
                          cy="12"
                          r="10"
                          stroke="currentColor"
                          strokeWidth="4"
                        ></circle>
                        <path
                          className="opacity-75"
                          fill="currentColor"
                          d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                        ></path>
                      </svg>
                      <span>Отправка...</span>
                    </div>
                  ) : countdown > 0 ? (
                    `Подождите ${countdown}с`
                  ) : (
                    "Получить код доступа"
                  )}
                </button>
              </div>

              <div className="text-center pt-4">
                <p className="text-[10px] font-bold text-slate-400 dark:text-slate-600 uppercase tracking-[0.2em]">
                  Новый пользователь? Обратитесь к администратору
                </p>
              </div>
            </form>
          ) : (
            <div className="text-center animate-fade-in">
              <div className="w-24 h-24 bg-green-100 dark:bg-green-900/30 rounded-full flex items-center justify-center mx-auto mb-8 relative">
                <span className="text-5xl animate-pulse">📩</span>
                <div className="absolute inset-0 bg-green-400 rounded-full animate-ping opacity-20"></div>
              </div>

              <h2 className="text-2xl font-black text-[#1E1B4B] dark:text-white mb-2">
                Проверьте почту
              </h2>
              <p className="text-sm font-medium text-slate-500 dark:text-slate-400 mb-8">
                Мы отправили 6-значный код на <br />
                <span className="text-[#4F46E5] dark:text-[#818CF8] font-bold">
                  {email}
                </span>
              </p>

              {isMailRu(email) && (
                <div className="mb-8 p-4 rounded-2xl bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800/30 text-amber-700 dark:text-amber-400 text-xs font-bold leading-relaxed animate-pulse">
                  ⚠️ Почтовые сервисы Mail.ru часто блокируют письма с кодами.
                  Если код не пришел в течение минуты, попросите разработчика
                  заменить вашу почту на другую (не Mail.ru).
                </div>
              )}

              <form onSubmit={handleOtpVerify} className="space-y-6">
                <div className="space-y-4">
                  <div className="flex justify-center">
                    <input
                      type="text"
                      value={otpCode}
                      onChange={(e) => setOtpCode(e.target.value)}
                      maxLength={6}
                      placeholder="······"
                      className="w-full max-w-[220px] px-2 py-4 bg-slate-50 dark:bg-[#1A1A1A] border-2 border-slate-200 dark:border-white/10 rounded-xl focus:ring-4 focus:ring-[#4F46E5]/20 focus:border-[#4F46E5] transition-all outline-none text-[#1E1B4B] dark:text-white text-center text-2xl tracking-[0.5em] font-black placeholder:text-slate-300 dark:placeholder:text-slate-800"
                      autoFocus
                    />
                  </div>

                  <div
                    className="flex items-center gap-3 p-1 cursor-pointer group"
                    onClick={() => setRememberMe(!rememberMe)}
                  >
                    <div
                      className={`w-6 h-6 shrink-0 rounded-lg border-2 flex items-center justify-center transition-all ${rememberMe ? "bg-[#4F46E5] border-[#4F46E5]" : "border-slate-300 dark:border-slate-600 group-hover:border-[#4F46E5]"}`}
                    >
                      {rememberMe && (
                        <svg
                          className="w-4 h-4 text-white"
                          fill="none"
                          viewBox="0 0 24 24"
                          stroke="currentColor"
                          strokeWidth="3"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            d="M5 13l4 4L19 7"
                          />
                        </svg>
                      )}
                    </div>
                    <div className="text-xs font-bold text-slate-600 dark:text-slate-400 leading-tight">
                      Запомнить меня на 2 месяца
                    </div>
                  </div>

                  <button
                    type="submit"
                    disabled={loading || otpCode.length < 6}
                    className="w-full py-4 px-6 rounded-2xl font-black text-white shadow-xl bg-gradient-to-r from-[#4F46E5] to-[#818CF8] hover:shadow-indigo-500/40 hover:-translate-y-1 transition-all duration-300 active:scale-95 disabled:opacity-50 disabled:cursor-not-allowed disabled:grayscale disabled:hover:translate-y-0"
                  >
                    {loading ? "Проверяем код..." : "Подтвердить и войти"}
                  </button>
                  {slowVerifyHint && (
                    <p className="mt-2 text-center text-xs text-amber-600 dark:text-amber-300">
                      Соединение медленное, ещё проверяем... Если не получится — отключите VPN и повторите.
                    </p>
                  )}
                </div>
              </form>

              <div className="mt-8">
                <p className="text-xs text-slate-400 dark:text-slate-500 italic">
                  Если письмо не пришло в течение минуты, проверьте папку
                  &quot;Спам&quot; или &quot;Рассылки&quot;.
                </p>
              </div>

              {message && (
                <div
                  className={`p-4 rounded-2xl text-sm text-center font-bold flex flex-col items-center justify-center gap-2 ${
                    message.includes("отправлено") || message.includes("✅")
                      ? "bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400 border border-green-200 dark:border-green-800"
                      : "bg-red-50 dark:bg-red-900/20 text-red-600 dark:text-red-400 border border-red-200 dark:border-red-800"
                  }`}
                >
                  <div className="flex items-center gap-2">
                    {message.includes("отправлено") ||
                    message.includes("✅") ? (
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        className="h-5 w-5 shrink-0"
                        viewBox="0 0 20 20"
                        fill="currentColor"
                      >
                        <path
                          fillRule="evenodd"
                          d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                          clipRule="evenodd"
                        />
                      </svg>
                    ) : (
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        className="h-5 w-5 shrink-0"
                        viewBox="0 0 20 20"
                        fill="currentColor"
                      >
                        <path
                          fillRule="evenodd"
                          d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z"
                          clipRule="evenodd"
                        />
                      </svg>
                    )}
                    <span>{message}</span>
                  </div>

                  {!(
                    message.includes("отправлено") || message.includes("✅")
                  ) &&
                    isMailRu(email) && (
                      <div className="mt-2 text-xs font-medium text-red-500 dark:text-red-300 leading-relaxed border-t border-red-200/50 dark:border-red-800/50 pt-2">
                        ⚠️ Замечена проблема с доставкой на Mail.ru. Если письмо
                        не приходит, попросите разработчика заменить почту на
                        другую.
                      </div>
                    )}
                </div>
              )}

              {message &&
                !message.includes("отправлено") &&
                !message.includes("✅") && (
                  <SupportContactButtons isMailRu={isMailRu(email)} />
                )}

              <div className="mt-10 flex flex-col gap-4">
                <button
                  onClick={() => {
                    setMagicLinkSent(false);
                    setMessage("");
                    setOtpCode("");
                  }}
                  className="text-xs font-bold text-[#4F46E5] dark:text-[#818CF8] hover:underline tracking-tight"
                >
                  ← Изменить адрес электронной почты
                </button>

                {countdown > 0 ? (
                  <div className="inline-flex items-center justify-center gap-2 text-[10px] font-black uppercase tracking-widest text-slate-400 dark:text-slate-600">
                    <span className="w-2 h-2 bg-slate-300 dark:bg-slate-700 rounded-full animate-ping"></span>
                    Повторный запрос через {countdown} сек
                  </div>
                ) : (
                  <button
                    type="button"
                    onClick={handleResend}
                    disabled={loading}
                    className="text-xs font-black text-[#4F46E5] dark:text-[#818CF8] hover:underline tracking-tight disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {loading ? "Отправляем..." : "↻ Выслать код повторно"}
                  </button>
                )}
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="mt-12 text-center space-y-2">
          <p className="text-[10px] font-bold text-slate-400 dark:text-slate-600 uppercase tracking-[0.3em]">
            &copy; 2026 Мои Горизонты &bull; Мои Горизонты
          </p>
          <p className="text-[10px] text-slate-400 dark:text-slate-600">
            Версия {packageJson.version}
          </p>
        </div>
      </div>
    </div>
  );
};

export default SimpleLoginPage;
